import { useEffect, useRef, useState, useCallback } from 'react';
import type { LogEntry } from '../api/types';

export type { LogEntry };

interface UseLogStreamReturn {
  entries: LogEntry[];
  connected: boolean;
  /** WebSocket 因未登录/无权限被拒(关闭码 4401)——此时不再自动重连 */
  authRequired: boolean;
  loading: boolean;
  latestSeq: number;
  total: number;
  hasMore: boolean;
  loadMore: () => Promise<boolean>;
  refresh: () => void;
}

/** 单次加载条数（初始加载 / 向上翻页 / 筛选补拉均使用该值） */
const PAGE_SIZE = 100;

/** 日志流：HTTP 按需加载历史 + WebSocket 批量实时推送 + 源头级别过滤。
 *
 * - ``levels`` 变化时**不清空重拉**：先对已加载日志做本地过滤，
 *   不足 ``PAGE_SIZE`` 条时再向后端补拉更早的筛选项
 * - 加载更早历史期间冻结实时插入，完成后一次性批量合并（避免列表抖动）
 */
export function useLogStream(
  levels: string[] = [],
  /** 按账户过滤，可多选：空数组不过滤；空串元素表示只看面板级日志；其余为账户名 */
  accounts: string[] = [],
): UseLogStreamReturn {
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [connected, setConnected] = useState(false);
  const [authRequired, setAuthRequired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [latestSeq, setLatestSeq] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const wsRef = useRef<WebSocket | null>(null);
  const lastSeqRef = useRef(0);
  const entriesRef = useRef<LogEntry[]>([]);
  const loadingMore = useRef(false);
  const frozenRef = useRef(false);          // 加载历史期间冻结实时插入
  const pendingLiveRef = useRef<LogEntry[]>([]); // 冻结期间暂存的实时日志
  const isFirstRunRef = useRef(true);

  const levelsKey = [...levels].sort().join(',');
  // 账户是数组，每次渲染都是新引用，不能直接进依赖表 —— 用 JSON 序列化出稳定键。
  // 不用 join(分隔符)：账户名由用户填写，可能本身含分隔符（空格、逗号等），
  // 那样 ["a b"] 与 ["a","b"] 会撞成同一个键，导致筛选变化被漏掉
  const accountsKey = JSON.stringify([...accounts].sort());
  const accountsRef = useRef<string[]>([]);
  accountsRef.current = accounts;
  // 把「级别 + 账户」压成一个查询串贯穿始终：筛选条件只需透传，无需在
  // 分页逻辑里分别处理，避免把两条筛选条件缠绕进本来就绕的翻页游标计算
  const buildQuery = useCallback((lvKey: string, accts: string[]) => {
    let q = lvKey ? `&levels=${encodeURIComponent(lvKey)}` : '';
    // 重复同名参数实现多选；空串元素表示「只看面板级」；空数组表示不过滤
    for (const a of accts) q += `&account=${encodeURIComponent(a)}`;
    return q;
  }, []);
  const filterRef = useRef('');
  filterRef.current = buildQuery(levelsKey, accounts);

  // ---- HTTP: 历史拉取（含源头级别与账户过滤）----
  const fetchHistory = useCallback(async (since: number, query: string) => {
    const token = localStorage.getItem('auth_token');
    const res = await fetch(
      `/api/logs/history?since=${since}&limit=${PAGE_SIZE}${query}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    const data = await res.json();
    return {
      entries: (data.entries || []) as LogEntry[],
      has_more: data.has_more ?? false,
      total: data.total ?? 0,
    };
  }, []);

  const loadHistory = useCallback(async (since: number, query: string) => {
    setLoading(true);
    try {
      const data = await fetchHistory(since, query);
      const merged = [...data.entries].sort((a, b) => a.seq_id - b.seq_id);
      entriesRef.current = merged;
      setEntries(merged);
      setHasMore(data.has_more);
      setTotal(data.total);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [fetchHistory]);

  /** 冻结实时插入，一次性合并暂存日志（loadMore / 筛选补拉结束时调用）。 */
  const flushPending = useCallback(() => {
    if (!pendingLiveRef.current.length) return;
    const existing = new Set(entriesRef.current.map((e) => e.seq_id));
    const newItems = pendingLiveRef.current.filter((e) => !existing.has(e.seq_id));
    pendingLiveRef.current = [];
    if (!newItems.length) return;
    // 新条目已在服务端缓冲中且匹配当前筛选 → total 同步 +N
    setTotal((prev) => prev + newItems.length);
    setEntries((prev) => {
      const merged = [...prev, ...newItems].sort((a, b) => a.seq_id - b.seq_id);
      entriesRef.current = merged;
      return merged;
    });
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingMore.current) return false;
    loadingMore.current = true;
    frozenRef.current = true; // 冻结实时插入，加载完成后一次性合并
    try {
      const oldest = entriesRef.current.length > 0 ? entriesRef.current[0].seq_id : 0;
      if (oldest <= 0) return false;
      const data = await fetchHistory(oldest, filterRef.current);
      if (!data.entries.length) return false;
      setEntries((prev) => {
        const existing = new Set(prev.map((e) => e.seq_id));
        const fresh = data.entries.filter((e) => !existing.has(e.seq_id));
        // 历史 + 冻结期间暂存的实时日志一次性合并
        const merged = [...fresh, ...prev, ...pendingLiveRef.current]
          .sort((a, b) => a.seq_id - b.seq_id);
        pendingLiveRef.current = [];
        entriesRef.current = merged;
        return merged;
      });
      setHasMore(data.has_more);
      setTotal(data.total);
      return data.has_more;
    } catch {
      return false;
    } finally {
      frozenRef.current = false;
      loadingMore.current = false;
    }
  }, [fetchHistory]);

  const refresh = useCallback(() => {
    // 重置并立即按当前筛选重拉
    entriesRef.current = [];
    pendingLiveRef.current = [];
    lastSeqRef.current = 0;
    setEntries([]);
    setLatestSeq(0);
    setTotal(0);
    setHasMore(false);
    setRefreshKey((k) => k + 1); // 触发 WS 重连
    loadHistory(0, filterRef.current);
  }, [loadHistory]);

  // ---- 挂载时初始加载 ----
  useEffect(() => {
    loadHistory(0, filterRef.current);
  }, [loadHistory]);

  // ---- 筛选变化：本地过滤 + 最新窗口合并 + 不足时向更早历史补拉 ----
  const applyFilter = useCallback(
    async (query: string, lvKey: string, accts: string[]) => {
    frozenRef.current = true;
    try {
      const levelSet = new Set(lvKey ? lvKey.split(',') : []);
      // 1. 先对已加载日志做本地过滤（空筛选 = 全部保留）
      const local = entriesRef.current.filter(
        (e) =>
          (levelSet.size === 0 || levelSet.has(e.level))
          && (accts.length === 0 || accts.includes(e.account ?? '')),
      );

      // 2. 只拉**最新一页**筛选结果并合并：加选 / 取消筛选后最近日志立即正确出现，
      //    更早的部分交给「加载更多」按需翻页 —— 与未筛选时的行为完全一致。
      //
      // ⚠️ 这里原先是个 while 循环，把匹配到的日志**一次全拉回来**。代价随数据量
      //    线性上涨：匹配 N 条就是 N/PAGE_SIZE 趟串行往返，每趟后端都要在环形
      //    缓冲里过滤一遍、前端还要把已积累的数组整体重排一次。筛选条件变化时
      //    本就会重连 WS，再叠上这一串请求，容易让某个请求撑过反向代理的超时，
      //    进而把后端标记为不可用 —— 表现是切筛选后报 503、刷新一下又正常。
      const latest = await fetchHistory(0, query);
      const existingLatest = new Set(local.map((e) => e.seq_id));
      const freshLatest = latest.entries.filter((e) => !existingLatest.has(e.seq_id));
      const current = [...local, ...freshLatest].sort((a, b) => a.seq_id - b.seq_id);

      entriesRef.current = current;
      setEntries(current);
      setHasMore(latest.has_more);
      setTotal(latest.total);
    } catch { /* ignore */ }
    finally {
      frozenRef.current = false;
      flushPending(); // 合并筛选期间暂存的实时日志
    }
    },
    [fetchHistory, flushPending],
  );

  useEffect(() => {
    if (isFirstRunRef.current) { isFirstRunRef.current = false; return; }
    // 从 ref 取当前账户数组：数组本身每次渲染都是新引用，依赖表里用 accountsKey
    applyFilter(buildQuery(levelsKey, accountsRef.current), levelsKey, accountsRef.current);
  }, [levelsKey, accountsKey, buildQuery, applyFilter]);

  // ---- WebSocket（筛选 / refreshKey 变化时重连）----
  useEffect(() => {
    let stopped = false;
    let reconnectTimer: ReturnType<typeof setTimeout>;
    let reconnectDelay = 1000;

    function connect() {
      if (stopped) return;
      const lastSeq = lastSeqRef.current;
      // 始终使用同源连接：经过反向代理时自动适配 HTTPS/WSS
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = window.location.host; // 含端口（非标准端口时）
      const q = buildQuery(levelsKey, accountsRef.current);
      // 浏览器 WebSocket API 无法设置 Authorization header,故 token 走查询参数
      const token = localStorage.getItem('auth_token');
      const tk = token ? `&token=${encodeURIComponent(token)}` : '';
      const wsUrl = `${wsProtocol}//${wsHost}/api/ws?last_seq=${lastSeq}${q}${tk}`;

      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          if (!stopped) {
            setConnected(true);
            setAuthRequired(false);
            reconnectDelay = 1000; // reset on success
          }
        };

        ws.onmessage = (event) => {
          if (stopped) return;
          try {
            const msg = JSON.parse(event.data);
            if (msg.type === 'logs' && Array.isArray(msg.entries)) {
              const fresh = msg.entries as LogEntry[];
              if (!fresh.length) return;
              const last = fresh[fresh.length - 1];
              lastSeqRef.current = Math.max(lastSeqRef.current, last.seq_id);
              setLatestSeq((prev) => Math.max(prev, last.seq_id));
              if (frozenRef.current) {
                // 冻结中：暂存，等待批量合并
                pendingLiveRef.current.push(...fresh);
                return;
              }
              const existing = new Set(entriesRef.current.map((e) => e.seq_id));
              const newItems = fresh.filter((e) => !existing.has(e.seq_id));
              if (newItems.length) {
                // 新条目已在服务端缓冲中且匹配当前筛选 → total 同步 +N
                setTotal((prev) => prev + newItems.length);
                setEntries((prev) => {
                  const merged = [...prev.slice(-1999), ...newItems];
                  entriesRef.current = merged;
                  return merged;
                });
              }
            } else if (msg.type === 'log' && msg.seq_id) {
              // 兼容单条推送（旧版后端）
              lastSeqRef.current = msg.seq_id;
              setLatestSeq((prev) => Math.max(prev, msg.seq_id));
              if (frozenRef.current) {
                pendingLiveRef.current.push(msg as LogEntry);
                return;
              }
              const exists = entriesRef.current.some((e) => e.seq_id === msg.seq_id);
              if (!exists) {
                setTotal((prev) => prev + 1);
                setEntries((prev) => {
                  const merged = [...prev.slice(-1999), msg as LogEntry];
                  entriesRef.current = merged;
                  return merged;
                });
              }
            }
          } catch { /* ignore */ }
        };

        ws.onclose = (event) => {
          if (!stopped) {
            setConnected(false);
            wsRef.current = null;
            if (event.code === 4401) {
              // 未登录 / 非管理员——重连也不会成功,直接停下并交由页面提示
              setAuthRequired(true);
              return;
            }
            reconnectTimer = setTimeout(connect, reconnectDelay);
            reconnectDelay = Math.min(reconnectDelay * 2, 30000); // 1s → 2s → 4s → ... → 30s max
          }
        };

        ws.onerror = () => {
          ws.close();
          wsRef.current = null;
        };
      } catch {
        if (!stopped) {
          reconnectTimer = setTimeout(connect, reconnectDelay);
        }
      }
    }

    connect();

    return () => {
      stopped = true;
      clearTimeout(reconnectTimer);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [refreshKey, levelsKey, accountsKey, buildQuery]);

  return { entries, connected, authRequired, loading, latestSeq, total, hasMore, loadMore, refresh };
}
